import Button from "@material-ui/core/Button"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogTitle from "@material-ui/core/DialogTitle"
import DialogContent from "@material-ui/core/DialogContent"
import InputAdornment from "@material-ui/core/InputAdornment"
import IconButton from "@material-ui/core/IconButton"
import { Eye, EyeOff } from "react-feather"
import { useZodSchemas } from "../../../hooks/use-zod-schemas"
import { UserViewModel } from "../../api/user"
import { Typography, useTheme } from "@material-ui/core"
import { useUserMutations } from "../../hooks/use-user-mutations"
import React from "react"
import { z } from "zod"
import { Form, Formik, FormikConfig } from "formik"
import { createFormikValidate } from "../../../utils/formik"
import FormikInput from "../../../components/FormikInput"
import Stack from "../../../components/Stack"
import { useTranslations } from "./use-translations"
import { useLazyGetPasswordResetTokenQuery } from "../../api"
import { useSnackbar } from "notistack"

const initialFormState: PasswordFormState = {
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
}

const passwordFieldType = {
  oldPassword: "password" as FieldType,
  newPassword: "password" as FieldType,
  confirmPassword: "password" as FieldType,
} as const

const ChagePasswordDialog = ({ user }: { user: UserViewModel }) => {
  const theme = useTheme()
  const { ZodPassword } = useZodSchemas()
  const translations = useTranslations()
  const [getPasswordResetToken] = useLazyGetPasswordResetTokenQuery()

  const { updateUserPassword } = useUserMutations()
  const [anchor, setAnchor] = React.useState<HTMLButtonElement | null>(null)
  const [fieldType, setFieldType] = React.useState<PasswordFormState>(passwordFieldType)
  const { enqueueSnackbar } = useSnackbar()

  const closeDialog = () => {
    setAnchor(null)
    setFieldType(passwordFieldType)
  }

  const PasswordFormSchema = z
    .object({
      oldPassword: ZodPassword,
      newPassword: ZodPassword,
      confirmPassword: ZodPassword,
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: translations.passwordsMustMatch,
      path: ["confirmPassword"],
    })

  const handleUpdatePassword = (passwordUpdateData: PasswordFormState) => {
    updateUserPassword(user.id, passwordUpdateData)
    closeDialog()
  }

  const handleGetPasswordResetToken = async () => {
    try {
      await getPasswordResetToken({ email: user.email, language: user.language })
      closeDialog()
      const message = translations.sendResetPasswordLinkSuccessMessage
      enqueueSnackbar(message, { variant: "success" })
    } catch (error) {
      enqueueSnackbar(translations.sendLinkFailedMessage, { variant: "error" })
    }
  }

  const formikProps: FormikConfig<PasswordFormState> = {
    initialValues: initialFormState,
    initialErrors: initialFormState,
    onSubmit: handleUpdatePassword,
    validate: createFormikValidate(PasswordFormSchema),
    validateOnMount: true,
  }

  const togglePasswordField = (field: keyof PasswordFormState) => {
    setFieldType((prev) => ({
      ...prev,
      [field]: prev[field] === "password" ? "text" : "password",
    }))
  }

  const oldPasswordInputEndAdornment = (
    <InputEndAdornment
      isOn={fieldType.oldPassword === "password"}
      toggleEye={() => togglePasswordField("oldPassword")}
    />
  )

  const newPasswordInputEndAdornment = (
    <InputEndAdornment
      isOn={fieldType.newPassword === "password"}
      toggleEye={() => togglePasswordField("newPassword")}
    />
  )
  const confirmPasswordInputEndAdornment = (
    <InputEndAdornment
      isOn={fieldType.confirmPassword === "password"}
      toggleEye={() => togglePasswordField("confirmPassword")}
    />
  )

  return (
    <>
      <Button
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        variant="outlined"
        style={{ borderColor: theme.palette.divider }}
      >
        {translations.changePassword}
      </Button>
      <Dialog
        open={Boolean(anchor)}
        onClose={closeDialog}
        PaperProps={{ style: { padding: "8px 8px 24px 8px" } }}
        fullWidth
      >
        <Formik {...formikProps}>
          {({ isSubmitting }) => {
            return (
              <Form>
                <DialogTitle>{translations.changePassword}</DialogTitle>
                <DialogContent>
                  <Stack>
                    <div style={{ marginBottom: 16 }}>
                      <FormikInput
                        id="oldPassword"
                        name="oldPassword"
                        variant="outlined"
                        type={fieldType.oldPassword}
                        label={translations.oldPassword}
                        disabled={isSubmitting}
                        InputProps={{ endAdornment: oldPasswordInputEndAdornment }}
                        fullWidth
                      />
                      <Typography variant="body2" color="textSecondary">
                        {translations.forgotPassword}.
                        <Button size="small" color="primary" onClick={handleGetPasswordResetToken}>
                          {translations.requestLink}
                        </Button>
                      </Typography>
                    </div>
                    <FormikInput
                      id="newPassword"
                      name="newPassword"
                      variant="outlined"
                      type={fieldType.newPassword}
                      label={translations.newPassword}
                      InputProps={{ endAdornment: newPasswordInputEndAdornment }}
                      disabled={isSubmitting}
                      fullWidth
                    />
                    <FormikInput
                      id="confirmPassword"
                      name="confirmPassword"
                      variant="outlined"
                      type={fieldType.confirmPassword}
                      label={translations.confirmPassword}
                      InputProps={{ endAdornment: confirmPasswordInputEndAdornment }}
                      disabled={isSubmitting}
                      fullWidth
                    />
                  </Stack>
                </DialogContent>
                <DialogActions style={{ marginLeft: 12, marginTop: 12, justifyContent: "flex-start" }}>
                  <Button type={"submit"} variant="contained" color="primary" size="small">
                    {translations.changePassword}
                  </Button>
                  <Button size="small" onClick={closeDialog}>
                    {translations.cancel}
                  </Button>
                </DialogActions>
              </Form>
            )
          }}
        </Formik>
      </Dialog>
    </>
  )
}

const InputEndAdornment = ({ isOn, toggleEye }: { isOn: boolean; toggleEye: () => void }) => {
  return (
    <InputAdornment position="end">
      <IconButton
        color="inherit"
        aria-label="toggle password visibility"
        onClick={toggleEye}
        onMouseDown={(e) => e.preventDefault()}
      >
        {isOn ? <Eye size={16} /> : <EyeOff size={16} />}
      </IconButton>
    </InputAdornment>
  )
}

export default ChagePasswordDialog

type PasswordFormState = {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}
type FieldType = "password" | "text"
